import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil } from 'lucide-react'
import { FormField } from '@/components/templates/entity-form'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getJSON, patchJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import { CustomPaymentTypesSettings } from '@/pages/CustomPaymentTypesSettings'
import { useViewOnly } from '@/components/templates/app-shell'
import { Badge } from '@/components/ui/badge'

/* eslint-disable max-lines -- Finance settings owns several cohesive settings sections. */

interface FinanceSettingsResponse {
  finance_settings: FinanceSettings
}

interface FinanceSettings {
  fiscal_year_start_month: number
  legal_name: string
  address_line1: string | null
  address_line2: string | null
  city: string | null
  region: string | null
  postal_code: string | null
  country_code: string | null
  company_number: string | null
  ein: string | null
  vat_number: string | null
  vat_registered: boolean
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

export function FinanceSettingsPage() {
  const viewOnly = useViewOnly()
  const queryClient = useQueryClient()
  const [editing, setEditing] = React.useState(false)
  const [month, setMonth] = React.useState('1')
  const settingsQuery = useQuery({
    queryKey: ['finance-settings'],
    queryFn: () => getJSON<FinanceSettingsResponse>('/api/v1/finance_settings'),
    retry: false,
  })
  const updateSettings = useMutation({
    mutationFn: () =>
      patchJSON<FinanceSettingsResponse>('/api/v1/finance_settings', {
        finance_settings: { fiscal_year_start_month: Number(month) },
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['finance-settings'], data)
      setEditing(false)
    },
  })
  const savedMonth = settingsQuery.data?.finance_settings.fiscal_year_start_month ?? 1
  const monthError = apiFieldMessages(updateSettings.error, 'fiscal_year_start_month')[0]

  const beginEditing = () => {
    setMonth(String(savedMonth))
    updateSettings.reset()
    setEditing(true)
  }

  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <header className="pb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-heading-sm font-semibold text-foreground">Finance settings</h1>
          {viewOnly && <Badge variant="info">View only</Badge>}
        </div>
      </header>

      {settingsQuery.isLoading ? (
        <p className="text-body-sm text-muted-foreground" role="status">
          Loading finance settings...
        </p>
      ) : settingsQuery.isError ? (
        <Alert>Finance settings unavailable</Alert>
      ) : (
        <Card className="max-w-2xl">
          <CardContent className="p-0">
            <LegalIdentitySettings
              settings={settingsQuery.data?.finance_settings}
              viewOnly={viewOnly}
            />
            <section className="p-4">
              <div className="flex items-center justify-between pb-3">
                <h2 className="text-body font-semibold text-foreground">Fiscal Year</h2>
                {!viewOnly && !editing && (
                  <Button
                    aria-label="Edit Fiscal Year"
                    className="h-8 w-8 border-0 bg-transparent p-0"
                    size="sm"
                    variant="outline"
                    onClick={beginEditing}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {!viewOnly && editing ? (
                <div className="max-w-sm">
                  <FormField label="Start month" htmlFor="fiscal-year-month" error={monthError}>
                    <Select value={month} onValueChange={setMonth}>
                      <SelectTrigger
                        id="fiscal-year-month"
                        aria-label="Fiscal Year start month"
                        className="h-9"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map((name, index) => (
                          <SelectItem key={name} value={String(index + 1)}>
                            {name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <div className="flex gap-2 pt-3">
                    <Button
                      size="sm"
                      disabled={updateSettings.isPending}
                      onClick={() => updateSettings.mutate()}
                    >
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-[minmax(9rem,1fr)_2fr] gap-4 text-body-sm">
                  <span className="text-muted-foreground">Start month</span>
                  <span className="text-foreground">{MONTHS[savedMonth - 1]}</span>
                </div>
              )}
            </section>
            <CustomPaymentTypesSettings />
            <GivingFooterSettings />
          </CardContent>
        </Card>
      )}
    </div>
  )
}

const COUNTRY_NAMES: Record<string, string> = {
  GB: 'United Kingdom',
  US: 'United States',
  IE: 'Ireland',
  CA: 'Canada',
}

function LegalIdentitySettings({
  settings,
  viewOnly,
}: {
  settings?: FinanceSettings
  viewOnly: boolean
}) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = React.useState(false)
  const [draft, setDraft] = React.useState<FinanceSettings | undefined>(settings)
  const save = useMutation({
    mutationFn: () =>
      patchJSON<FinanceSettingsResponse>('/api/v1/finance_settings', {
        finance_settings: {
          legal_name: draft?.legal_name,
          address_line1: draft?.address_line1,
          address_line2: draft?.address_line2,
          city: draft?.city,
          region: draft?.region,
          postal_code: draft?.postal_code,
          country_code: draft?.country_code,
          company_number: draft?.company_number,
          vat_number: draft?.vat_number,
          ein: draft?.ein,
        },
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['finance-settings'], data)
      setEditing(false)
    },
  })

  React.useEffect(() => {
    if (settings && !editing) setDraft(settings)
  }, [settings, editing])

  if (!settings) return null

  return (
    <section className="border-b border-border p-4">
      <div className="flex items-center justify-between pb-3">
        <div className="flex items-center gap-2">
          <h2 className="text-body font-semibold text-foreground">Legal identity</h2>
          {settings.vat_registered && <Badge variant="success">VAT registered</Badge>}
        </div>
        {!viewOnly && !editing && (
          <Button
            aria-label="Edit Legal identity"
            className="h-8 w-8 border-0 bg-transparent p-0"
            size="sm"
            variant="outline"
            onClick={() => {
              save.reset()
              setDraft(settings)
              setEditing(true)
            }}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>
      {editing && draft ? (
        <LegalIdentityEditForm
          draft={draft}
          setDraft={setDraft}
          save={save}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <LegalIdentityReadState settings={settings} />
      )}
    </section>
  )
}

function LegalIdentityEditForm({
  draft,
  setDraft,
  save,
  onCancel,
}: {
  draft: FinanceSettings
  setDraft: React.Dispatch<React.SetStateAction<FinanceSettings | undefined>>
  save: ReturnType<typeof useMutation<FinanceSettingsResponse>>
  onCancel: () => void
}) {
  const update = (field: keyof FinanceSettings, value: string) =>
    setDraft((current) => (current ? { ...current, [field]: value } : current))
  const field = (label: string, id: string, key: keyof FinanceSettings) => (
    <LegalIdentityField
      label={label}
      id={id}
      value={String(draft[key] ?? '')}
      onChange={(value) => update(key, value)}
    />
  )
  return (
    <form
      className="max-w-2xl space-y-3"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      {field('Legal name', 'legal-name', 'legal_name')}
      {field('Address line 1', 'address-line-1', 'address_line1')}
      {field('Address line 2', 'address-line-2', 'address_line2')}
      <div className="grid grid-cols-2 gap-4">
        {field('City', 'city', 'city')}
        {field('Region', 'region', 'region')}
      </div>
      <div className="grid grid-cols-2 gap-4">
        {field('Postal code', 'postal-code', 'postal_code')}
        <FormField label="Country" htmlFor="country-code">
          <Select
            value={draft.country_code ?? ''}
            onValueChange={(value) => update('country_code', value)}
          >
            <SelectTrigger id="country-code" className="h-9">
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(COUNTRY_NAMES).map(([code, name]) => (
                <SelectItem key={code} value={code}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      </div>
      {field('Company number', 'company-number', 'company_number')}
      {field('VAT number', 'vat-number', 'vat_number')}
      {field('EIN', 'ein', 'ein')}
      {save.error && <Alert>{save.error.message}</Alert>}
      <div className="flex gap-2 pt-2">
        <Button type="submit" size="sm" disabled={save.isPending}>
          {save.isPending ? 'Saving...' : 'Save'}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

function LegalIdentityReadState({ settings }: { settings: FinanceSettings }) {
  const address = [
    settings.address_line1,
    settings.address_line2,
    [settings.city, settings.region, settings.postal_code].filter(Boolean).join(', '),
  ].filter(Boolean)
  return (
    <div className="grid grid-cols-[minmax(9rem,1fr)_2fr] gap-4 text-body-sm">
      {settings.legal_name && <LegalIdentityRow label="Legal name" value={settings.legal_name} />}
      {address.length > 0 && (
        <LegalIdentityRow
          label="Address"
          value={
            <>
              {address.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </>
          }
        />
      )}
      {settings.country_code && (
        <LegalIdentityRow
          label="Country"
          value={COUNTRY_NAMES[settings.country_code] ?? settings.country_code}
        />
      )}
      {settings.company_number && (
        <LegalIdentityRow label="Company number" value={settings.company_number} />
      )}
      {settings.vat_number && <LegalIdentityRow label="VAT number" value={settings.vat_number} />}
      {settings.ein && <LegalIdentityRow label="EIN" value={settings.ein} />}
    </div>
  )
}

function LegalIdentityField({
  label,
  id,
  value,
  onChange,
}: {
  label: string
  id: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <FormField label={label} htmlFor={id}>
      <Input
        id={id}
        className="h-9"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </FormField>
  )
}

function LegalIdentityRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground">{value}</span>
    </>
  )
}

function GivingFooterSettings() {
  const viewOnly = useViewOnly()
  const queryClient = useQueryClient()
  const settings = useQuery({
    queryKey: ['giving-settings'],
    queryFn: () =>
      getJSON<{
        donation_settings: {
          disclosures_footer: string | null
          giving_preset_amounts_cents: number[]
        }
      }>('/api/v1/donation_settings'),
    retry: false,
  })
  const [footer, setFooter] = React.useState('')
  const [presets, setPresets] = React.useState('')
  const save = useMutation({
    mutationFn: () =>
      patchJSON('/api/v1/donation_settings', {
        donation_settings: {
          disclosures_footer: footer,
          giving_preset_amounts_cents: presets
            .split(',')
            .map((value) => Math.round(Number(value.trim()) * 100)),
        },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['giving-settings'] }),
  })
  React.useEffect(() => {
    if (settings.data?.donation_settings) {
      setFooter(settings.data.donation_settings.disclosures_footer ?? '')
      setPresets(
        settings.data.donation_settings.giving_preset_amounts_cents
          .map((cents) => (cents / 100).toFixed(2))
          .join(', '),
      )
    }
  }, [settings.data])

  if (settings.isLoading || settings.isError) return null
  return (
    <section className="border-t border-border p-4">
      <h2 className="pb-1 text-body font-semibold text-foreground">Give disclosures footer</h2>
      <p className="pb-3 text-body-sm text-muted-foreground">
        Optional guidance shown below the public give flow. Tax-status wording is managed
        separately.
      </p>
      {viewOnly ? (
        <dl className="grid max-w-2xl gap-3 text-body-sm">
          <div>
            <dt className="text-muted-foreground">Footer text</dt>
            <dd className="whitespace-pre-wrap text-foreground">{footer || '—'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Preset gift amounts</dt>
            <dd className="text-foreground">{presets || '—'}</dd>
          </div>
        </dl>
      ) : (
        <>
          <form
            className="max-w-2xl space-y-3"
            onSubmit={(event) => {
              event.preventDefault()
              save.mutate()
            }}
          >
            <FormField label="Footer text" htmlFor="giving-disclosures-footer">
              <Textarea
                id="giving-disclosures-footer"
                value={footer}
                onChange={(event) => setFooter(event.target.value)}
              />
            </FormField>
            <Button type="submit" size="sm" disabled={save.isPending}>
              {save.isPending ? 'Saving...' : 'Save footer'}
            </Button>
          </form>
          <form
            className="mt-4 max-w-2xl space-y-3"
            onSubmit={(event) => {
              event.preventDefault()
              save.mutate()
            }}
          >
            <FormField label="Preset gift amounts" htmlFor="giving-preset-amounts">
              <Input
                id="giving-preset-amounts"
                inputMode="decimal"
                value={presets}
                onChange={(event) => setPresets(event.target.value)}
              />
            </FormField>
            <p className="text-caption text-muted-foreground">
              Comma-separated dollar amounts used when a campaign has no custom presets.
            </p>
            <Button type="submit" size="sm" disabled={save.isPending}>
              Save preset amounts
            </Button>
          </form>
        </>
      )}
    </section>
  )
}
